import { createApi } from '@reduxjs/toolkit/query/react';
import { saveAs } from 'file-saver';
import { createBaseQuery } from 'helpers/api';

export const subChecksApi = createApi({
  reducerPath: 'subChecksApi',
  baseQuery: createBaseQuery('sub_checks', { timeout: 0 }),
  endpoints: builder => ({
    createSubCheck: builder.mutation({
      query: params => ({
        url: '/',
        method: 'POST',
        body: params,
      }),
      transformResponse: ({ result }) => result,
    }),
    updateSubCheck: builder.mutation({
      query: ({ id, params }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: params,
      }),
      transformResponse: ({ result }) => result,
    }),
    updateSubCheckAdmin: builder.mutation({
      query: ({ id, params }) => ({
        url: `/${id}/admin`,
        method: 'PUT',
        body: params,
      }),
      transformResponse: ({ result }) => result,
    }),
    deferSubCheck: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/defer`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    updateSubCheckSupportUser: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/update_support_user`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    bulkUpdateSubCheckSupportUsers: builder.mutation({
      query: data => ({
        url: `/bulk/update_support_users`,
        method: 'POST',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    bulkSubCheckAssignmentSummary: builder.mutation({
      query: data => ({
        url: `/bulk/assignment_summary`,
        method: 'POST',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    deleteSubCheck: builder.mutation({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      transformResponse: ({ result }) => result,
    }),
    getSubCheckResult: builder.query({
      queryFn: async (
        { subCheck, check, candidate, isFile = true },
        api,
        extraOptions,
        baseQuery,
      ) => {
        const { data: result } = await baseQuery({
          url: `/${subCheck.id}/result`,
          method: 'GET',
          responseHandler: async response => {
            if (isFile) {
              const blob = await response.blob();
              return blob;
            } else {
              const jsonResponse = await response.json();
              return jsonResponse.result;
            }
          },
          cache: 'no-cache',
        });
        if (isFile)
          saveAs(
            result,
            `${candidate.first_name}_${candidate.last_name}-${check.type}-${subCheck.title}`,
          );
        else window.open(result, '_blank');

        return { data: null };
      },
    }),
    subCheckLogViewResult: builder.mutation({
      query: id => ({
        url: `/${id}/result/log`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useCreateSubCheckMutation,
  useUpdateSubCheckMutation,
  useUpdateSubCheckAdminMutation,
  useUpdateSubCheckSupportUserMutation,
  useBulkUpdateSubCheckSupportUsersMutation,
  useBulkSubCheckAssignmentSummaryMutation,
  useDeferSubCheckMutation,
  useDeleteSubCheckMutation,
  useLazyGetSubCheckResultQuery,
  useSubCheckLogViewResultMutation,
} = subChecksApi;
